'use client';

import React from 'react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '../ui/combobox';

export interface SourceComboboxOption {
  value: string;
  label: string;
}

interface SourceComboboxProps {
  /** Accepts plain string[] or {value, label}[] objects */
  items: readonly string[] | readonly SourceComboboxOption[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  id?: string;
  disabled?: boolean;
  className?: string;
  emptyText?: string;
}

export const SourceCombobox: React.FC<SourceComboboxProps> = ({
  items,
  placeholder = 'Select an option',
  value,
  onChange,
  id,
  disabled,
  className,
  emptyText = 'No items found.',
}) => {
  // Normalize items to {value, label}[] regardless of input shape
  const normalizedOptions: SourceComboboxOption[] = (
    items as readonly (string | SourceComboboxOption)[]
  ).map(item =>
    typeof item === 'string' ? { value: item, label: item } : item
  );

  const labels = normalizedOptions.map(opt => opt.label);

  // Find the display label for the currently selected value
  const selectedOption = normalizedOptions.find(opt => opt.value === value);
  const displayLabel = selectedOption ? selectedOption.label : (value ?? '');

  const handleValueChange = (newLabel: string) => {
    const matchedOpt = normalizedOptions.find(
      opt =>
        opt.label.toLowerCase() === newLabel.toLowerCase() ||
        opt.value.toLowerCase() === newLabel.toLowerCase()
    );
    if (matchedOpt) {
      onChange?.(matchedOpt.value);
    } else {
      onChange?.(newLabel);
    }
  };

  return (
    <Combobox
      items={labels}
      value={displayLabel}
      onValueChange={handleValueChange}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
      />
      <ComboboxContent className="z-50">
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList
          renderItem={(label: string) => {
            const opt = normalizedOptions.find(o => o.label === label);
            const optValue = opt ? opt.value : label;
            return (
              <ComboboxItem key={optValue} value={label}>
                {label}
              </ComboboxItem>
            );
          }}
        />
      </ComboboxContent>
    </Combobox>
  );
};

export default SourceCombobox;
